/*
 * Copyright (c) 2025 Sila Artificial Intelligence Research LLC
 * A DA Holding Ltd Company
 * All rights reserved.
 *
 * PROPRIETARY AND CONFIDENTIAL
 * This software and its documentation are proprietary to Sila Artificial
 * Intelligence Research LLC. Unauthorized copying, distribution, or use
 * of this software is strictly prohibited.
 *
 * THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
 * IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY,
 * FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL
 * THE AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER
 * LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING
 * FROM, OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER
 * DEALINGS IN THE SOFTWARE.
 */

import React from 'react';
import { Outlet, useLoaderData } from 'react-router-dom';
import { MeProvider } from '../contexts/MeContext';
import { FiltersProvider } from '../contexts/FiltersContext';

interface LoaderData {
  org: string;
}

/**
 * Auth-initialized route wrapper.
 *
 * Authentication is handled by the router loader before this component renders.
 * This component receives the authenticated user data from the loader and
 * provides it to child components via context.
 */
const AuthInitializer: React.FC = () => {
  const loaderData = useLoaderData() as LoaderData | null;

  // If loader returned null (redirecting to login), show loading
  if (!loaderData) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="flex flex-col items-center gap-4">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-purple-500"></div>
          <p className="text-muted-foreground text-sm">Redirecting to login...</p>
        </div>
      </div>
    );
  }

  // Authenticated - render children with user data
  return (
    <MeProvider org={loaderData.org}>
      <FiltersProvider>
        <div className="min-h-screen w-full">
          <Outlet />
        </div>
      </FiltersProvider>
    </MeProvider>
  );
};

export default AuthInitializer;
